// API 配置和请求函数
// 使用 Next.js API 路由代理，避免跨域问题
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api';

// 会话信息类型
export interface SessionInfo {
  id: string;
  device: string;
  ip_address: string;
  created_at: string;
  last_accessed_at: string;
  expires_at: string;
  is_valid: boolean;
  is_expired: boolean;
  is_current?: boolean;
}

// 获取存储的token
export const getToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('token');
};

// 设置token（支持刷新token）
export const setToken = (token: string, refreshToken?: string) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem('token', token);
  if (refreshToken) {
    localStorage.setItem('refresh_token', refreshToken);
  }
};

// 获取刷新token
export const getRefreshToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('refresh_token');
};

// 清除token（包括刷新token）
export const clearToken = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
};

// 获取用户信息
export const getUser = () => {
  if (typeof window === 'undefined') return null;
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

// 设置用户信息
export const setUser = (user: unknown) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem('user', JSON.stringify(user));
};

// API请求封装（支持自动刷新token）
export const apiRequest = async <T = unknown>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const token = getToken();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (options.headers) {
    Object.assign(headers, options.headers);
  }

  // 对于需要认证的auth端点（如profile），也需要添加token
  // 只有登录和注册端点不需要token
  if (token && !endpoint.includes('/auth/login') && !endpoint.includes('/auth/register')) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = `${API_BASE_URL}${endpoint}`;
  
  // 添加请求日志
  if (endpoint.includes('statistics')) {
    console.log('[apiRequest] Making request to:', url);
    console.log('[apiRequest] Headers:', headers);
  }
  
  try {
    let response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // 重要：包含Cookie（用于验证码token）
    });
    
    // 添加响应日志
    if (endpoint.includes('statistics')) {
      console.log('[apiRequest] Response status:', response.status);
      console.log('[apiRequest] Response ok:', response.ok);
    }

    // 如果token过期，尝试刷新
    if (response.status === 401 && !endpoint.includes('/auth/') && getRefreshToken()) {
      try {
        const refreshResponse = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: getRefreshToken() }),
        });

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json() as {
            access_token: string;
            refresh_token: string;
            user: unknown;
          };
          setToken(refreshData.access_token, refreshData.refresh_token);
          if (refreshData.user) setUser(refreshData.user);
          
          // 使用新token重试请求
          headers['Authorization'] = `Bearer ${refreshData.access_token}`;
          response = await fetch(url, {
            ...options,
            headers,
            credentials: 'include',
          });
        } else {
          // 刷新失败，清除token并跳转登录
          clearToken();
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
          throw new Error('Token expired and refresh failed');
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        clearToken();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        throw error;
      }
    }

    // 检查响应状态
    let data;
    try {
      const responseText = await response.text();
      if (endpoint.includes('statistics')) {
        console.log('[apiRequest] Response text:', responseText);
      }
      if (responseText) {
        data = JSON.parse(responseText);
        if (endpoint.includes('statistics')) {
          console.log('[apiRequest] Parsed JSON data:', data);
        }
      } else {
        data = {};
      }
    } catch (parseError) {
      // 如果不是JSON响应，构造错误
      if (endpoint.includes('statistics')) {
        console.error('[apiRequest] Failed to parse JSON:', parseError);
      }
      if (!response.ok) {
        const error = new Error(`请求失败 (${response.status})`) as Error & { response?: { status?: number; data?: { message?: string } } };
        error.response = {
          status: response.status,
          data: { message: `请求失败 (${response.status})` },
        };
        throw error;
      }
      return {} as T;
    }

    if (!response.ok) {
      // 统一错误格式
      const errorMessage = data?.message || data?.data?.message || `请求失败 (${response.status})`;
      const error = new Error(errorMessage) as Error & { 
        response?: { 
          status?: number; 
          data?: { 
            message?: string; 
            code?: number;
          } 
        } 
      };
      error.response = {
        status: response.status,
        data: {
          message: errorMessage,
          code: data?.code || response.status,
        },
      };
      throw error;
    }

    return data;
  } catch (error) {
    // 如果是网络错误
    if (error instanceof TypeError && error.message.includes('fetch')) {
      const networkError = new Error('网络请求失败，请检查网络连接') as Error & { 
        response?: { 
          status?: number; 
          data?: { message?: string } 
        } 
      };
      networkError.response = {
        status: 0,
        data: { message: '网络请求失败，请检查网络连接' },
      };
      throw networkError;
    }

    // 如果已经是格式化的错误，直接抛出
    if (error instanceof Error && 'response' in error) {
      console.error('API Error:', error);
      // 导入错误消息映射（避免循环依赖）
      const { extractErrorMessage } = await import('./error-messages');
      const friendlyError = new Error(extractErrorMessage(error)) as Error & { 
        response?: { 
          status?: number; 
          data?: { message?: string; code?: number } 
        } 
      };
      friendlyError.response = (error as Error & { response?: { status?: number; data?: { message?: string; code?: number } } }).response;
      throw friendlyError;
    }

    // 其他错误
    console.error('API Error:', error);
    const { extractErrorMessage } = await import('./error-messages');
    throw new Error(extractErrorMessage(error));
  }
};

// 认证相关API
export const authApi = {
  register: (data: { username: string; password: string; email: string; role?: string; captcha_key?: string }) =>
    apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  login: (data: { username: string; password: string; captcha_key?: string }) =>
    apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getProfile: () => apiRequest('/auth/profile'),
  
  // 检查认证状态和token有效性
  checkStatus: () => apiRequest('/auth/status'),
  
  // 列出所有活跃的session（管理员）
  listSessions: () => apiRequest('/auth/sessions'),
  
  // 删除指定用户的session（管理员）
  deleteUserSession: (userId: number) =>
    apiRequest(`/auth/sessions?user_id=${userId}`, {
      method: 'DELETE',
    }),
  
  // 刷新token
  refreshToken: (refreshToken: string) =>
    apiRequest('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),
  
  // 获取我的会话列表
  getMySessions: () => apiRequest<{ code: number; data: { sessions: SessionInfo[]; count: number } }>('/auth/my-sessions'),
  
  // 删除我的会话
  deleteMySession: (sessionId: string) => apiRequest<{ code: number; message: string }>(`/auth/my-sessions/${sessionId}`, {
    method: 'DELETE',
  }),

  updateProfile: (data: {
    username?: string;
    email?: string;
    real_name?: string;
    student_id?: string;
    phone?: string;
    password?: string;
  }) =>
    apiRequest('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  uploadAvatar: (formData: FormData) =>
    apiRequest('/auth/upload-avatar', {
      method: 'POST',
      body: formData,
      headers: {}, // 不设置Content-Type，让浏览器自动设置multipart/form-data
    }),
};

// 题库API
export const questionBankApi = {
  list: (params?: { subject_id?: number }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return apiRequest(`/question-banks${query ? `?${query}` : ''}`);
  },
  
  get: (id: number) => apiRequest(`/question-banks/${id}`),
  
  create: (data: { name: string; description: string; subject_id?: number }) =>
    apiRequest('/question-banks', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: { name: string; description: string; subject_id?: number }) =>
    apiRequest(`/question-banks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: number) =>
    apiRequest(`/question-banks/${id}`, {
      method: 'DELETE',
    }),
  
  getStats: (id: number) => apiRequest(`/question-banks/${id}/stats`),
};

// 题目API
export const questionApi = {
  list: (params?: { bank_id?: number; type?: string; difficulty?: string }) => {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    return apiRequest(`/questions${query ? `?${query}` : ''}`);
  },
  
  get: (id: number, showAnswer?: boolean) =>
    apiRequest(`/questions/${id}${showAnswer ? '?show_answer=true' : ''}`),
  
  create: (data: Record<string, unknown>) =>
    apiRequest('/questions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Record<string, unknown>) =>
    apiRequest(`/questions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  delete: (id: number) =>
    apiRequest(`/questions/${id}`, {
      method: 'DELETE',
    }),
  
  batchCreate: (questions: Array<Record<string, unknown>>) =>
    apiRequest('/questions/batch', {
      method: 'POST',
      body: JSON.stringify(questions),
    }),
};

// 试卷API
export const examPaperApi = {
  list: () => apiRequest('/exam-papers'),
  
  get: (id: number) => apiRequest(`/exam-papers/${id}`),
  
  create: (data: Record<string, unknown>) =>
    apiRequest('/exam-papers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Record<string, unknown>) =>
    apiRequest(`/exam-papers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  publish: (id: number) =>
    apiRequest(`/exam-papers/${id}/publish`, {
      method: 'POST',
    }),
  
  archive: (id: number) =>
    apiRequest(`/exam-papers/${id}/archive`, {
      method: 'POST',
    }),
  
  delete: (id: number) =>
    apiRequest(`/exam-papers/${id}`, {
      method: 'DELETE',
    }),
};

// 考试API
export const examApi = {
  start: (paperId: number) =>
    apiRequest('/exams/start', {
      method: 'POST',
      body: JSON.stringify({ paper_id: paperId }),
    }),
  
  submit: (examRecordId: number, answers: Array<Record<string, unknown>>) =>
    apiRequest('/exams/submit', {
      method: 'POST',
      body: JSON.stringify({
        exam_record_id: examRecordId,
        answers,
      }),
    }),
  
  getMyRecords: () => apiRequest('/exams/my-records'),
  
  getRecord: (id: number) => apiRequest(`/exams/records/${id}`),
  
  getStatistics: (paperId: number) =>
    apiRequest(`/exams/papers/${paperId}/statistics`),
  
  // 获取考试记录的答题详情
  getExamAnswers: (recordId: number) =>
    apiRequest<Array<Record<string, unknown>>>(`/history/exam-records/${recordId}/answers`, {
      method: 'GET',
    }),
};

// 历史记录API
export const historyApi = {
  // 获取考试记录详情
  getExamRecord: (recordId: number) =>
    apiRequest<Record<string, unknown>>(`/history/exam-records/${recordId}`, {
      method: 'GET',
    }),
  
  getAllExams: (params?: {
    student_id?: string;
    paper_id?: string;
    status?: string;
    page?: number;
    page_size?: number;
    sort?: string;
    order?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const queryString = searchParams.toString();
    return apiRequest(`/history/all-exams${queryString ? `?${queryString}` : ''}`);
  },
  
  getMyExams: (params?: {
    page?: number;
    page_size?: number;
    sort?: string;
    order?: 'asc' | 'desc';
  }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const queryString = searchParams.toString();
    return apiRequest(`/history/my-exams${queryString ? `?${queryString}` : ''}`);
  },
};

// 系统配置API
export const configApi = {
  getAll: () => apiRequest('/config'),
  
  get: (key: string) => apiRequest(`/config/${key}`),
  
  update: (data: Record<string, string>) =>
    apiRequest('/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// 登录日志API
export const loginLogApi = {
  // 获取登录日志列表
  list: (params?: {
    page?: number;
    page_size?: number;
    user_id?: number;
    username?: string;
    ip_address?: string;
    success?: boolean;
    start_date?: string;
    end_date?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const queryString = searchParams.toString();
    return apiRequest(`/login-logs${queryString ? `?${queryString}` : ''}`);
  },
  
  // 清理登录日志
  clear: (params?: {
    days?: number;
    before_date?: string;
    user_id?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const queryString = searchParams.toString();
    return apiRequest(`/login-logs${queryString ? `?${queryString}` : ''}`, {
      method: 'DELETE',
    });
  },
};

// 系统信息API（公开接口）
export const systemApi = {
  getInfo: () => apiRequest('/system/info'),
};

// 一言API
export const hitokotoApi = {
  // 获取一言（公开接口）
  get: () => apiRequest('/hitokoto'),
  
  // 获取自定义一言列表（教师）
  getCustomList: () => apiRequest('/hitokoto/custom'),
  
  // 创建自定义一言（教师）
  create: (data: { content: string; source: string; enabled: boolean }) =>
    apiRequest('/hitokoto/custom', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 更新自定义一言（教师）
  update: (id: number, data: { content: string; source: string; enabled: boolean }) =>
    apiRequest(`/hitokoto/custom/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  // 删除自定义一言（教师）
  delete: (id: number) =>
    apiRequest(`/hitokoto/custom/${id}`, {
      method: 'DELETE',
    }),
};

// 验证码API
export const captchaApi = {
  generate: (type?: 'digit' | 'slide' | 'pow' | 'click' | 'turnstile' | 'random') =>
    apiRequest<{
      id: string;
      type: string;
      image?: string;
      images?: string[];
      total_images?: number;
      challenge?: unknown;
      sitekey?: string;
      text?: string;
      expires_at?: string;
    }>('/captcha/generate', {
      method: 'POST',
      body: JSON.stringify({ type: type || '' }),
    }),
  
  verify: (id: string, code?: string, solution?: string, positions?: Array<{ x: number; y: number }>, selectedIndices?: number[], solutions?: number[] | number[][]) =>
    apiRequest<{ verified: boolean; key: string }>('/captcha/verify', {
      method: 'POST',
      body: JSON.stringify({ 
        id, 
        code: code || '',
        solutions, // POW解决方案（批量，点阵数组格式）
        selected_indices: selectedIndices, // 图片选择验证码：用户选中的图片索引数组
      }),
    }),
  
  // POW专用接口
  powRedeem: (token: string, solutions: number[]) =>
    apiRequest<{ success: boolean; token?: string; expires?: number; message?: string }>('/captcha/pow/redeem', {
      method: 'POST',
      body: JSON.stringify({ token, solutions }),
    }),
  
  powValidate: (token: string) =>
    apiRequest<{ success: boolean; message?: string }>('/captcha/pow/validate', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  
  // Turnstile验证（由前端组件处理，后端验证token）
  verifyTurnstile: (id: string, token: string) =>
    apiRequest<{ verified: boolean; key: string }>('/captcha/verify', {
      method: 'POST',
      body: JSON.stringify({ id, turnstile_token: token }),
    }),
};

// 错题本API
export const wrongQuestionApi = {
  // 获取我的错题列表
  list: (unmastered?: boolean) => {
    const params = new URLSearchParams();
    if (unmastered) {
      params.append('unmastered', 'true');
    }
    const queryString = params.toString();
    return apiRequest(`/wrong-questions/my${queryString ? `?${queryString}` : ''}`);
  },
  
  // 标记题目已掌握
  markMastered: (questionId: string) =>
    apiRequest(`/wrong-questions/${questionId}/mastered`, {
      method: 'PUT',
    }),
};

// 用户管理API
export const userApi = {
  // 获取用户列表
  list: (params?: {
    role?: string;
    group_id?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }) => {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }
    const queryString = searchParams.toString();
    return apiRequest(`/users${queryString ? `?${queryString}` : ''}`);
  },
  
  // 获取用户详情
  get: (userId: number) => apiRequest(`/users/${userId}`),
  
  // 重置密码
  resetPassword: (data: { user_id: number; new_password: string }) =>
    apiRequest('/users/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 更新角色
  updateRole: (data: { user_id: number; role: string }) =>
    apiRequest('/users/update-role', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 更新用户组
  updateGroup: (data: { user_id: number; group_id: number | null }) =>
    apiRequest('/users/update-group', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 批量重置密码
  batchResetPassword: (data: { user_ids: number[]; new_password: string }) =>
    apiRequest('/users/batch/reset-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 批量更新用户组
  batchUpdateGroup: (data: { user_ids: number[]; group_id: number | null }) =>
    apiRequest('/users/batch/update-group', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 批量更新用户状态
  batchUpdateStatus: (data: { user_ids: number[]; status: string }) =>
    apiRequest('/users/batch/update-status', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 批量更新用户角色
  batchUpdateRole: (data: { user_ids: number[]; role: string }) =>
    apiRequest('/users/batch/update-role', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 批量删除用户
  batchDelete: (data: { user_ids: number[] }) =>
    apiRequest('/users/batch/delete', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 批量添加到组
  batchAddToGroup: (data: { user_ids: number[]; group_id: number }) =>
    apiRequest('/users/batch/add-to-group', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 批量从组移除
  batchRemoveFromGroup: (data: { user_ids: number[]; group_id: number }) =>
    apiRequest('/users/batch/remove-from-group', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 删除用户
  delete: (userId: number) =>
    apiRequest(`/users/${userId}`, {
      method: 'DELETE',
    }),
  
  // 更新用户信息（教师权限）
  updateInfo: (data: { 
    user_id: number; 
    email: string; 
    real_name?: string; 
    student_id?: string; 
    phone?: string;
  }) =>
    apiRequest('/users/update-info', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// 用户组API
export const userGroupApi = {
  // 获取用户组列表
  list: () => apiRequest('/user-groups'),
  
  // 创建用户组
  create: (data: { name: string; description: string }) =>
    apiRequest('/user-groups', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 更新用户组
  update: (id: number, data: { name: string; description: string }) =>
    apiRequest(`/user-groups/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  // 删除用户组
  delete: (id: number) =>
    apiRequest(`/user-groups/${id}`, {
      method: 'DELETE',
    }),
};

// 科目API
export const subjectApi = {
  // 获取科目列表
  list: () => apiRequest('/subjects'),
  
  // 创建科目
  create: (data: { name: string; description: string }) =>
    apiRequest('/subjects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 更新科目
  update: (id: number, data: { name: string; description: string }) =>
    apiRequest(`/subjects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  // 删除科目
  delete: (id: number) =>
    apiRequest(`/subjects/${id}`, {
      method: 'DELETE',
    }),
};

// 等级API
export const levelApi = {
  // 获取等级列表
  list: () => apiRequest('/levels'),
  
  // 创建等级
  create: (data: { name: string; description: string; sort_order: number }) =>
    apiRequest('/levels', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 更新等级
  update: (id: number, data: { name: string; description: string; sort_order: number }) =>
    apiRequest(`/levels/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  // 删除等级
  delete: (id: number) =>
    apiRequest(`/levels/${id}`, {
      method: 'DELETE',
    }),
};

// 试卷权限API
export const paperPermissionApi = {
  // 获取权限列表
  list: () => apiRequest('/paper-permissions'),
  
  // 获取试卷权限列表
  getPaperPermissions: (paperId: number) =>
    apiRequest(`/paper-permissions/paper/${paperId}`),
  
  // 创建权限（支持用户/组/标签三种方式）
  create: (data: {
    paper_id: number | null;
    user_id?: number | null;    // 用户授权
    group_id?: number | null;   // 组授权
    tag_id?: number | null;      // 标签授权
    can_view: boolean;
    can_take: boolean;
  }) =>
    apiRequest('/paper-permissions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 删除权限
  delete: (id: number) =>
    apiRequest(`/paper-permissions/${id}`, {
      method: 'DELETE',
    }),
};

// 聊天室API
export const chatApi = {
  // 获取聊天室列表
  listRooms: () => apiRequest('/chat/rooms'),
  
  // 获取聊天室配置
  getConfig: () => apiRequest('/chat/config'),
  
  // 更新聊天室配置
  updateConfig: (config: {
    chat_enabled: boolean;
    exam_chat_enabled: boolean;
    allow_non_exam_chat_during_exam: boolean;
    default_room_id: number;
  }) =>
    apiRequest('/chat/config', {
      method: 'PUT',
      body: JSON.stringify(config),
    }),
  
  // 创建聊天室
  createRoom: (data: {
    name: string;
    type: 'public' | 'group' | 'exam';
    group_id?: number;
    description?: string;
    max_members?: number;
    enabled?: boolean;
    exam_only?: boolean;
  }) =>
    apiRequest('/chat/rooms', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 更新聊天室
  updateRoom: (id: number, data: {
    name: string;
    description?: string;
    max_members?: number;
    enabled?: boolean;
    exam_only?: boolean;
  }) =>
    apiRequest(`/chat/rooms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  
  // 删除聊天室
  deleteRoom: (id: number) =>
    apiRequest(`/chat/rooms/${id}`, {
      method: 'DELETE',
    }),
  
  // 获取聊天室消息历史
  getMessages: (roomId: number) =>
    apiRequest(`/chat/rooms/${roomId}/messages`),
};

// 导入API
export const importApi = {
  // 批量导入用户（CSV）
  importUsersCSV: (csvContent: string) =>
    apiRequest('/import/users/csv', {
      method: 'POST',
      body: csvContent,
      headers: {
        'Content-Type': 'text/csv',
      },
    }),

  // 批量导入用户（JSON）
  importUsersJSON: (data: {
    users: Array<{
      username: string;
      password: string;
      email: string;
      role?: string;
      real_name?: string;
      student_id?: string;
      phone?: string;
      group_id?: number;
    }>;
  }) =>
    apiRequest('/import/users/json', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  // 批量导入题目（CSV）
  importQuestionsCSV: (csvContent: string) =>
    apiRequest('/import/questions/csv', {
      method: 'POST',
      body: csvContent,
      headers: {
        'Content-Type': 'text/csv',
      },
    }),
  
  // 批量导入题目（JSON）
  importQuestionsJSON: (data: {
    bank_id: number;
    questions: Array<{
      type: string;
      content: string;
      options: string[];
      answer: string;
      score: number;
    }>;
  }) =>
    apiRequest('/import/questions/json', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// 导出API
export const exportApi = {
  // 导出试卷模板
  getPaperTemplate: () => apiRequest('/export/paper-template'),
  
  // 导出题目模板
  getQuestionTemplate: () => apiRequest('/export/question-template'),
  
  // 导出考试成绩（支持多维度筛选）- 返回文本数据而不是JSON
  exportExamResults: async (params?: {
    paper_id?: number;
    user_ids?: number[];
    group_ids?: number[];
    tag_ids?: number[];
    user_id?: number; // 向后兼容
    group_id?: number; // 向后兼容
    tag_id?: number; // 向后兼容
    start_date?: string;
    end_date?: string;
    min_score?: number;
    max_score?: number;
    status?: string;
    format?: 'csv' | 'excel';
    include_details?: boolean;
    include_non_taken?: boolean;
  }): Promise<string> => {
    const token = getToken();
    const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api';
    
    // 构建URL - 如果有paper_id且没有其他筛选条件，使用GET；否则使用POST
    const hasFilters = params && (
      params.user_ids?.length || params.group_ids?.length || params.tag_ids?.length ||
      params.user_id || params.group_id || params.tag_id ||
      params.start_date || params.end_date || params.min_score !== undefined ||
      params.max_score !== undefined || params.status || params.include_non_taken
    );
    
    const url = params?.paper_id && !hasFilters
      ? `${API_BASE_URL}/export/exam-results/${params.paper_id}` 
      : `${API_BASE_URL}/export/exam-results`;
    
    const headers: HeadersInit = {};
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
    
    const options: RequestInit = {
      headers,
      credentials: 'include',
    };
    
    // 如果有筛选条件或paper_id，使用POST方法
    if (params && (hasFilters || params.paper_id)) {
      options.method = 'POST';
      (headers as Record<string, string>)['Content-Type'] = 'application/json';
      options.body = JSON.stringify(params);
    } else {
      options.method = 'GET';
    }
    
    const response = await fetch(url, options);
    
    if (!response.ok) {
      // 尝试解析错误信息
      try {
        const errorData = await response.json();
        throw new Error(errorData.message || `导出失败 (${response.status})`);
      } catch {
        throw new Error(`导出失败 (${response.status})`);
      }
    }
    
    // 返回文本内容（CSV）
    return await response.text();
  },
};

// 统计分析API
export const statisticsApi = {
  // 获取系统整体统计（教师）
  getSystemStatistics: () => apiRequest('/statistics/system'),
  // 获取仪表盘数据（教师）
  getDashboardData: () => apiRequest('/statistics/dashboard'),
  // 获取试卷统计
  getPaperStatistics: (paperId: number) => {
    console.log('[API] getPaperStatistics called with paperId:', paperId);
    const endpoint = `/exam-papers/${paperId}/statistics`;
    console.log('[API] Requesting endpoint:', endpoint);
    return apiRequest(endpoint).then((result) => {
      console.log('[API] getPaperStatistics response:', result);
      return result;
    }).catch((error) => {
      console.error('[API] getPaperStatistics error:', error);
      throw error;
    });
  },
  // 获取用户统计
  getUserStatistics: (userId: number) =>
    apiRequest(`/statistics/users/${userId}`),
  // 获取题库统计
  getQuestionBankStatistics: (bankId: number) =>
    apiRequest(`/statistics/question-banks/${bankId}`),
};

// 学生API
export const studentApi = {
  // 获取学生列表
  list: () => apiRequest('/students'),
};

// WebSocket/在线通讯API
export const websocketApi = {
  // 获取在线用户列表
  getOnlineUsers: () => apiRequest<{ count: number; users: Array<{ user_id: number; username: string; role: string; connected_at: string }> }>('/websocket/online-users'),
  // 获取在线用户数量
  getOnlineCount: () => apiRequest<{ count: number }>('/websocket/online-count'),
};

// 防作弊API
export const antiCheatApi = {
  // 获取防作弊统计
  getStatistics: () => apiRequest('/anti-cheat/statistics'),
  // 获取考试的防作弊记录
  getExamLogs: (examId: number) => apiRequest(`/anti-cheat/exam/${examId}`),
  // 获取用户的防作弊记录
  getUserLogs: (userId: number) => apiRequest(`/anti-cheat/user/${userId}`),
  // 记录防作弊事件（学生端调用）
  logEvent: (data: {
    exam_record_id: number;
    event_type: string;
    event_data?: string;
  }) =>
    apiRequest('/anti-cheat/log', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 刷新token
  refreshToken: (refreshToken: string) =>
    apiRequest<{
      access_token: string;
      refresh_token: string;
      expires_in: number;
      refresh_expires_in: number;
      token: string;
      user: User;
    }>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),
};

// Tag管理API
export const tagApi = {
  // 获取标签列表
  getTags: () =>
    apiRequest<Tag[]>('/tags', {
      method: 'GET',
    }),

  // 获取标签详情
  getTag: (id: number) =>
    apiRequest<Tag>(`/tags/${id}`, {
      method: 'GET',
    }),

  // 创建标签
  createTag: (data: {
    name: string;
    description?: string;
    color?: string;
  }) =>
    apiRequest<Tag>('/tags', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 更新标签
  updateTag: (id: number, data: {
    name?: string;
    description?: string;
    color?: string;
  }) =>
    apiRequest<Tag>(`/tags/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // 删除标签
  deleteTag: (id: number) =>
    apiRequest<{ message: string }>(`/tags/${id}`, {
      method: 'DELETE',
    }),

  // 获取用户标签
  getUserTags: (userId: number) =>
    apiRequest<Tag[]>(`/tags/user-tags?user_id=${userId}`, {
      method: 'GET',
    }),

  // 为用户添加标签
  addUserTag: (data: { user_id: number; tag_id: number }) =>
    apiRequest<{ message: string }>('/tags/add-user', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 移除用户标签
  removeUserTag: (data: { user_id: number; tag_id: number }) =>
    apiRequest<{ message: string }>('/tags/remove-user', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 批量更新用户标签
  batchUpdateUserTags: (data: {
    user_id: number;
    tag_ids: number[];
  }) =>
    apiRequest<{ message: string }>('/tags/batch-update-user', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// 人工评分API
export const gradingApi = {
  // 获取待评分列表
  getPendingGradingRecords: (params?: {
    paper_id?: number;
    status?: string;
    manual_grading_status?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.paper_id) queryParams.append('paper_id', params.paper_id.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.manual_grading_status !== undefined) {
      queryParams.append('manual_grading_status', params.manual_grading_status.toString());
    }
    return apiRequest<Array<{
      id: number;
      exam_record_id: number;
      paper_id: number;
      paper_title: string;
      student_id: number;
      student_name: string;
      auto_score: number;
      manual_score: number | null;
      total_score: number;
      manual_grading_status: number;
      submit_time: string;
      essay_questions: Array<{
        question_id: number;
        question_content: string;
        answer: string;
        max_score: number;
        manual_score: number | null;
      }>;
    }>>(`/grading/pending?${queryParams.toString()}`, {
      method: 'GET',
    });
  },

  // 提交人工评分
  submitGrading: (data: {
    exam_record_id: number;
    scores: Array<{ question_id: number; score: number }>;
    total_manual_score: number;
    notes?: string;
  }) =>
    apiRequest<{
      message: string;
    }>('/grading/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 对问答题进行评分
  gradeEssayQuestion: (data: {
    exam_record_id: number;
    question_id: number;
    score: number;
    notes?: string;
  }) =>
    apiRequest<{
      exam_record_id: number;
      question_id: number;
      manual_score: number;
      total_score: number;
      auto_score: number;
      manual_total_score: number;
      graded_count: number;
      total_essay_count: number;
      is_completed: boolean;
    }>('/grading/essay', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // 获取考试记录的问答题
  getExamRecordEssayQuestions: (recordId: number) =>
    apiRequest<Array<{
      id: number;
      question_id: number;
      content: string;
      max_score: number;
      answer: string;
      auto_score: number;
      manual_score?: number;
      is_graded: boolean;
      order_num?: number;
    }>>(`/grading/records/${recordId}/essays`, {
      method: 'GET',
    }),
};

// 类型定义
export interface Tag {
  id: number;
  name: string;
  description?: string;
  color?: string;
  created_by: number;
  created_at: string;
  updated_at: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  student_id?: string;
  real_name?: string;
  phone?: string;
  avatar_url?: string;
}
